import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { MultiCartFacade } from '@spartacus/cart/base/root'
import { OCC_USER_ID_CURRENT } from '@spartacus/core'
import { Order, OrderHistoryList } from '@spartacus/order/root'
import { Observable, Subject, of } from 'rxjs'
import { catchError, delay, retry, switchMap, tap } from 'rxjs/operators'
import { OrderHistory, OrderStatus } from '../models/enum/order.enum'
import { SharedValue } from '../models/enum/shared.enum'
import { StockStatus } from '../models/enum/stock.enum'
import { IExcludedItem } from '../models/interface/excluded-cart-item'
import { ICustomOrderHistory } from '../models/interface/order-history-interface'
import { ICustomOrderHistoryList } from '../models/orderHistoryList.model'
import { AppConfigurationService } from './app-configuration.service'
import { CustomCartService } from './custom-cart.service'
import { SharedService } from './shared/shared.service'

@Injectable({
  providedIn: 'root'
})
export class OrderHistoryService {
  constructor(
    private http: HttpClient,
    private appConfigurationService: AppConfigurationService,
    private customCartService: CustomCartService,
    private multiCartService: MultiCartFacade,
    private sharedService: SharedService
  ) {}

  private itemCancelledSource = new Subject<Order>()
  public itemCancelled$ = this.itemCancelledSource.asObservable()

  private orderCancelledSource = new Subject<Order>()
  public orderCancelled$ = this.orderCancelledSource.asObservable()

  public excludedItems: IExcludedItem[]

  public getOrdersHistory(orderHistory: ICustomOrderHistory): Observable<OrderHistoryList> {
    let payload: HttpParams = new HttpParams()
    if (orderHistory.currentPage) {
      payload = payload.set(OrderHistory.CURRENT_PAGE, (orderHistory.currentPage - 1).toString())
    }
    if (orderHistory.sortCode) {
      payload = payload.set(OrderHistory.SORT, orderHistory.sortCode)
    }
    if (orderHistory.filterId) {
      payload = payload.set(OrderHistory.STATUSES, orderHistory.filterId)
      payload = payload.set(OrderHistory.ISLINEFILTER, orderHistory.isLinefilter)
    }

    if (orderHistory.code) {
      payload = payload.set(OrderHistory.CODE, orderHistory.code)
    } else {
      payload = payload.set(OrderHistory.PAGE_SIZE, SharedValue.pageSize.toString())
    }

    const url = `${this.appConfigurationService.getBaseSiteUrl()}/users/current/orders/searchByCode`
    return this.http.get(url, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        return of(this.getEmptyOrderHistoryList())
      })
    )
  }

  public cancelFullOrder(order: Order): Observable<any> {
    // getting all the items
    const payload = {
      cancellationRequestEntryInputs: order.entries.map((itemOrder) => {
        return { orderEntryNumber: itemOrder.entryNumber, quantity: itemOrder.quantity }
      })
    }

    return this.cancelOrder(order.code, payload)
  }

  public cancelPartialOrder(order: Order, item): Observable<any> {
    const payload = {
      cancellationRequestEntryInputs: [{ orderEntryNumber: item.entryNumber, quantity: item.quantity }]
    }

    return this.cancelOrder(order.code, payload)
  }

  private cancelOrder(orderCode: string, payload: any): Observable<any> {
    const url = `${this.appConfigurationService.getBaseSiteUrl()}/users/current/orders/${orderCode}/cancellation`

    return this.http.post(url, payload).pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public checkStatusForFullCancellation(status: string): boolean {
    switch (status) {
      // allow cancellation
      case OrderStatus.CONFIRMED:
      case OrderStatus.CREATED:
        return true

      default:
        return false
    }
  }

  public checkStatusForPartialCancellation(status: string): boolean {
    switch (status) {
      // allow cancellation
      case 'Back-ordered':
      case 'Created':
      case 'Confirmed':
        return true

      default:
        return false
    }
  }

  public itemCancelled(order: Order): void {
    this.itemCancelledSource.next(order)
  }

  public orderCancelled(order: Order): void {
    this.orderCancelledSource.next(order)
  }

  public getOrderStatusColor(order: Order, forceStatus: OrderStatus): string {
    const status = forceStatus || order?.status

    switch (status) {
      case OrderStatus.COMPLETED:
        return 'tag--green'
      case OrderStatus.CANCELLED:
        return 'tag--red'
      default:
        return 'tag--default'
    }
  }

  public getEmptyOrderHistoryList(): any {
    return {
      orders: [],
      pagination: {
        currentPage: 0,
        pageSize: 5,
        sort: 'byDate',
        totalPages: 0,
        totalResults: 0
      },
      sorts: [
        {
          code: 'byDate',
          selected: true
        },
        {
          code: 'byOrderNumber',
          selected: false
        }
      ]
    }
  }

  public orderAgain(code: string): Observable<any> {
    const params: HttpParams = new HttpParams().set('fields', 'FULL').set('orderCode', code)

    const url = `${this.appConfigurationService.getBaseSiteUrl()}/orgUsers/current/cartFromOrder`
    return this.http.post<any>(url, null, { params }).pipe(
      /* AC-1871 remove
        tap((res) => {
             this.excludedItems = this.getExcludedItemsOnReorder(res?.cartModifications);
        }),
         */
      switchMap(() => {
        return this.customCartService.getCurrentCart().pipe(
          tap((cart) => this.loadReorderedCart(cart?.code)),
          delay(2000)
        )
      }),
      retry(1),
      catchError(this.sharedService.handleError)
    )
  }

  public getOrderById(orderCode: string): Observable<Order> {
    const params: HttpParams = new HttpParams().set('fields', 'FULL')
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/orders/${orderCode}`
    return this.http.get<any>(url, { params }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  private getExcludedItemsOnReorder(excludedItems: any[]): any[] {
    return excludedItems?.map((item) => {
      return {
        code: item.entry.product.code,
        name: item.entry.product.name,
        statusCode: item.statusCode,
        stockLevelStatus: item.entry.product.stock.stockLevelStatus,
        show: true
      }
    })
  }

  public loadReorderedCart(cartCode): void {
    if (!cartCode) {
      return
    }

    this.multiCartService.loadCart({
      cartId: cartCode,
      userId: OCC_USER_ID_CURRENT,
      extraData: { active: true }
    })
  }

  public getReasonToExclude(item: IExcludedItem): string {
    switch (item.statusCode) {
      case StockStatus.unavailable:
        return `${item.name} ${AppConfigurationService.settings.reorder.unavailable}`
      case StockStatus.lowStock:
        return `${item.name} ${AppConfigurationService.settings.reorder.lowStock}`
      case StockStatus.noStock:
        return `${item.name} ${AppConfigurationService.settings.reorder.noStock}`
    }
  }

  public getOrderHistoryCsv(selectedFilter?: string): Observable<ICustomOrderHistoryList> {
    let params: HttpParams = new HttpParams()
    if (selectedFilter) {
      params = params.set('statuses', selectedFilter)
    }
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/orders/historyCsv`
    return this.http.get<any>(url, { params }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public checkOrderCreditClaim(orderDocumentsNumber?: string[]): boolean {
    let canRaiseCreditClaim: boolean
    orderDocumentsNumber && orderDocumentsNumber.length > 0
      ? (canRaiseCreditClaim = true)
      : (canRaiseCreditClaim = false)
    return canRaiseCreditClaim
  }
}
